import axios from 'axios';
import { getToken } from './auth';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend.chamafruits.com';

const withAuth = () => ({
  headers: { Authorization: `Bearer ${getToken()}` },
  withCredentials: true,
});

export const getBarcodePricingRule = async () =>
  (await axios.get(`${API_URL}/settings/barcode-pricing-rule`, withAuth())).data;

export const updateBarcodePricingRule = async (rule) =>
  (await axios.put(`${API_URL}/settings/barcode-pricing-rule`, rule || {}, withAuth())).data;

export const getCompanySaleRoundingSettings = async () =>
  (await axios.get(`${API_URL}/settings/sale-rounding`, withAuth())).data;

export const updateCompanySaleRoundingSettings = async (settings) =>
  (await axios.put(`${API_URL}/settings/sale-rounding`, settings, withAuth())).data;

export const getSaleRoundingSettings = async (branchId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/sale-rounding/settings`, withAuth())).data;

export const updateBranchSaleRoundingSettings = async (branchId, settings) =>
  (await axios.put(`${API_URL}/branch/${branchId}/sale-rounding/settings`, settings, withAuth())).data;

export const clearBranchSaleRoundingSettings = async (branchId) =>
  (await axios.delete(`${API_URL}/branch/${branchId}/sale-rounding/settings`, withAuth())).data;

export const getBranchRequestConfig = async () =>
  (await axios.get(`${API_URL}/settings/branch-request-config`, withAuth())).data;

export const updateBranchRequestConfig = async (config) =>
  (await axios.put(`${API_URL}/settings/branch-request-config`, config || {}, withAuth())).data;

export const getPosSoldOutDisplaySettings = async () =>
  (await axios.get(`${API_URL}/settings/pos-sold-out-display`, withAuth())).data;

export const updatePosSoldOutDisplaySettings = async (settings) =>
  (await axios.put(`${API_URL}/settings/pos-sold-out-display`, settings || {}, withAuth())).data;

export const getProductSearchSettings = async () =>
  (await axios.get(`${API_URL}/settings/product-search`, withAuth())).data;

export const updateProductSearchSettings = async (settings) =>
  (await axios.put(`${API_URL}/settings/product-search`, settings || {}, withAuth())).data;

export const getClientPricingSettings = async () =>
  (await axios.get(`${API_URL}/settings/client-pricing`, withAuth())).data;

export const updateClientPricingSettings = async (settings) =>
  (await axios.put(`${API_URL}/settings/client-pricing`, settings || {}, withAuth())).data;

export const getClientPricingMarginConversionIssues = async (params = {}) =>
  (await axios.get(`${API_URL}/settings/client-pricing/margin-conversion-issues`, {
    ...withAuth(),
    params,
  })).data;

export const retryClientPricingMarginConversions = async () =>
  (await axios.post(`${API_URL}/settings/client-pricing/margin-conversions/retry`, {}, withAuth())).data;

export const getPrintReceiptRedirectDefaults = async () =>
  (await axios.get(`${API_URL}/settings/print-receipt-redirect-defaults`, withAuth())).data;

export const updatePrintReceiptRedirectDefaults = async (settings) =>
  (await axios.put(`${API_URL}/settings/print-receipt-redirect-defaults`, settings || {}, withAuth())).data;

export const getCategoryBarcodes = async ({ includeInactive = false } = {}) => {
  const params = new URLSearchParams();
  if (includeInactive) params.set('includeInactive', 'true');
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return (await axios.get(`${API_URL}/settings/category-barcodes${suffix}`, withAuth())).data;
};

export const regenerateCategoryBarcodes = async () =>
  (await axios.post(`${API_URL}/settings/category-barcodes/regenerate`, {}, withAuth())).data;

export const getPurchaseOrderReasonsSettings = async () =>
  (await axios.get(`${API_URL}/settings/purchase-order-reasons`, withAuth())).data;

export const updatePurchaseOrderReasonsSettings = async (reasons) =>
  (await axios.put(`${API_URL}/settings/purchase-order-reasons`, reasons || {}, withAuth())).data;

export const getPurchaseOrderNotificationSettings = async () =>
  (await axios.get(`${API_URL}/settings/purchase-order-notifications`, withAuth())).data;

export const updatePurchaseOrderNotificationSettings = async (settings) =>
  (await axios.put(`${API_URL}/settings/purchase-order-notifications`, settings || {}, withAuth())).data;

export const getProductFormats = async (params = {}) =>
  (await axios.get(`${API_URL}/settings/product-formats`, { ...withAuth(), params })).data;

export const createProductFormat = async (data) =>
  (await axios.post(`${API_URL}/settings/product-formats`, data || {}, withAuth())).data;

export const updateProductFormat = async (id, data) =>
  (await axios.patch(`${API_URL}/settings/product-formats/${id}`, data || {}, withAuth())).data;

export const getClientPricingCategories = async (params = {}) =>
  (await axios.get(`${API_URL}/settings/client-pricing-categories`, { ...withAuth(), params })).data;

export const getClientPricingApplicabilityOptions = async () =>
  (await axios.get(`${API_URL}/settings/client-pricing-applicability-options`, withAuth())).data;

export const createClientPricingCategory = async (data) =>
  (await axios.post(`${API_URL}/settings/client-pricing-categories`, data || {}, withAuth())).data;

export const updateClientPricingCategory = async (id, data) =>
  (await axios.patch(`${API_URL}/settings/client-pricing-categories/${id}`, data || {}, withAuth())).data;

export const deleteClientPricingCategory = async (id) =>
  (await axios.delete(`${API_URL}/settings/client-pricing-categories/${id}`, withAuth())).data;

export const getClientPricingDiscountCatalog = async (id, params = {}) =>
  (await axios.get(`${API_URL}/settings/client-pricing-categories/${id}/discounts`, { ...withAuth(), params })).data;

export const updateClientPricingDiscount = async (categoryId, varietyPriceId, data) =>
  (await axios.put(
    `${API_URL}/settings/client-pricing-categories/${categoryId}/discounts/${varietyPriceId}`,
    data || {},
    withAuth()
  )).data;

export const bulkUpdateClientPricingDiscounts = async (categoryId, data) =>
  (await axios.post(
    `${API_URL}/settings/client-pricing-categories/${categoryId}/discounts/bulk`,
    data || {},
    withAuth()
  )).data;

export const getPriceRecommendationSettings = async (branchId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/price-recommendations/settings`, withAuth())).data;

export const updatePriceRecommendationSettings = async (branchId, settings) =>
  (await axios.put(`${API_URL}/branch/${branchId}/price-recommendations/settings`, settings || {}, withAuth())).data;

export const getClientPricingGroups = async () =>
  (await axios.get(`${API_URL}/settings/client-pricing-groups`, withAuth())).data;
export const createClientPricingGroup = async (data) =>
  (await axios.post(`${API_URL}/settings/client-pricing-groups`, data, withAuth())).data;
export const updateClientPricingGroup = async (id, data) =>
  (await axios.patch(`${API_URL}/settings/client-pricing-groups/${id}`, data, withAuth())).data;
export const deleteClientPricingGroup = async (id) =>
  (await axios.delete(`${API_URL}/settings/client-pricing-groups/${id}`, withAuth())).data;
export const previewClientPricingSelection = async (clientPricingCategoryIds, params = {}) =>
  (await axios.post(`${API_URL}/settings/client-pricing-categories/preview`, { ...params, clientPricingCategoryIds }, withAuth())).data;
